import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useDayPlan } from "../../DayPlanProvider";
import { PlanItemRow } from "./PlanItemRow";
import { CustomItemForm } from "./CustomItemForm";
import { useToast } from "../../../../contexts/ToastContext";

export const PlanList: React.FC = () => {
  const { planItems, showCustomItemForm, reorderItems } = useDayPlan();
  const { addToast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = planItems.findIndex((item) => item.id === active.id);
      const newIndex = planItems.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        try {
          await reorderItems(oldIndex, newIndex);
        } catch (error) {
          console.error("Failed to reorder items:", error);
          addToast({ type: "error", message: "Nie udało się zmienić kolejności pozycji" });
        }
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Nagłówek */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">PLAN DNIA</h2>
        <div className="text-sm text-gray-500">{planItems.length} pozycji</div>
      </div>

      {/* Tabela z elementami */}
      {planItems.length > 0 && (
        <div className="overflow-x-auto">
          <div className="min-w-full">
            {/* Nagłówki tabeli */}
            <div
              className="grid gap-2 py-3 px-4 bg-gray-50 rounded-t-lg border border-gray-200 text-sm font-medium text-gray-700"
              style={{
                gridTemplateColumns:
                  "32px 72px 72px 1fr 80px 100px 80px 100px 40px",
              }}
            >
              <div className="flex items-center justify-center">⋮⋯</div>
              <div className="flex items-center justify-center">Od</div>
              <div className="flex items-center justify-center">Do</div>
              <div className="flex items-center">Element</div>
              <div className="flex items-center justify-center">Ilość</div>
              <div className="flex items-center justify-center">Cena jedn.</div>
              <div className="flex items-center justify-center">Rabat</div>
              <div className="flex items-center justify-center">Suma</div>
              <div className="flex items-center justify-center"></div>
            </div>

            {/* Lista elementów z drag & drop */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={planItems.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-1">
                  {planItems.map((item) => (
                    <PlanItemRow
                      key={item.id}
                      item={item}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>
      )}

      {/* Pusty stan - chowany, gdy otwarty jest formularz pozycji własnej */}
      {planItems.length === 0 && !showCustomItemForm && (
        <div className="text-center py-12 text-gray-500">
          <div className="text-lg mb-2">Brak elementów w planie dnia</div>
          <div className="text-sm">
            Dodaj pierwsze elementy używając przycisków poniżej
          </div>
        </div>
      )}

      {/* Formularz dodawania pozycji własnej - niezależny od tego, czy plan dnia
          ma już pozycje (dzień z samymi noclegami renderuje pustą listę) */}
      {showCustomItemForm && (
        <div className="mt-2">
          <CustomItemForm />
        </div>
      )}
    </div>
  );
};
